/**
 * Curated, deterministic content for the landing page.
 *
 * Everything here is hand-picked so animations can be choreographed to exact
 * values frame-by-frame — there are no live API calls. Logos point at stable
 * public CDNs (coingecko for crypto, Trust Wallet for chains); every asset
 * also carries a brand `color` so a colored initials chip can stand in where
 * we intentionally avoid remote logos, or if an image ever fails to load.
 *
 * NOTE: stats + prices are realistic placeholders — confirm real numbers with
 * marketing/data before launch.
 */

export type AssetKind = "crypto" | "stock" | "stable";

export interface Asset {
  symbol: string;
  name: string;
  logo?: string;
  /** Brand color, used for the fallback chip + subtle glow. */
  color: string;
  /** Optional filled chip background for transparent logo marks. */
  iconBackground?: string;
  kind: AssetKind;
  /** Display price string (curated). */
  price?: string;
  /** 24h change in percent (curated). */
  change24h?: number;
}

export interface Chain {
  id: string;
  name: string;
  logo: string;
  color: string;
}

export interface Basket {
  id: string;
  name: string;
  tagline: string;
  /** 7-day performance in percent (curated). */
  change7d: number;
  accent: string;
  constituents: Asset[];
}

export interface PredictionMarket {
  id: string;
  question: string;
  category: string;
  /** Probability of "Yes" as a percent, 0–100 (curated). */
  yes: number;
  volume: string;
  endsIn: string;
}

export interface Stat {
  label: string;
  value: number;
  prefix?: string;
  suffix?: string;
}

const CG = "https://assets.coingecko.com/coins/images";
const CGI = "https://coin-images.coingecko.com/coins/images";
// Trust Wallet 256px chain logos via the jsDelivr CDN (the previous icon source
// served ~400-byte thumbnails that rendered blurry).
const CHAIN = "https://cdn.jsdelivr.net/gh/trustwallet/assets/blockchains";

// ---------------------------------------------------------------------------
// Crypto
// ---------------------------------------------------------------------------
export const CRYPTO: Record<string, Asset> = {
  BTC: { symbol: "BTC", name: "Bitcoin", logo: "/assets/coins/bitcoin-btc-logo.svg", color: "#f7931a", kind: "crypto", price: "$68,420", change24h: 2.4 },
  ETH: { symbol: "ETH", name: "Ethereum", logo: "/assets/coins/ethereum-eth-logo.svg", color: "#627eea", kind: "crypto", price: "$3,560", change24h: 1.8 },
  SOL: { symbol: "SOL", name: "Solana", logo: "/assets/coins/solana-sol-logo.svg", color: "#14f195", iconBackground: "#111116", kind: "crypto", price: "$172.30", change24h: 5.1 },
  BONK: { symbol: "BONK", name: "Bonk", logo: `${CGI}/28600/large/bonk.jpg`, color: "#f7a531", kind: "crypto", price: "$0.000028", change24h: 9.3 },
  WIF: { symbol: "WIF", name: "dogwifhat", logo: `${CGI}/33566/large/dogwifhat.jpg`, color: "#c4a07a", kind: "crypto", price: "$2.41", change24h: -3.2 },
  JUP: { symbol: "JUP", name: "Jupiter", logo: `${CGI}/34188/large/jup.png`, color: "#22c55e", kind: "crypto", price: "$0.92", change24h: 4.0 },
  RENDER: { symbol: "RENDER", name: "Render", logo: `${CG}/11636/large/rndr.png`, color: "#cf1a2b", kind: "crypto", price: "$7.85", change24h: 6.7 },
  LINK: { symbol: "LINK", name: "Chainlink", logo: `${CG}/877/large/chainlink-new-logo.png`, color: "#2a5ada", kind: "crypto", price: "$14.20", change24h: 1.1 },
  ONDO: { symbol: "ONDO", name: "Ondo", logo: `${CGI}/26580/large/ONDO.png`, color: "#2563eb", kind: "crypto", price: "$1.04", change24h: 3.5 },
};

export const STABLES: Record<string, Asset> = {
  USDC: { symbol: "USDC", name: "USD Coin", logo: "/assets/coins/usd-coin-usdc-logo.svg", color: "#2775ca", kind: "stable", price: "$1.00", change24h: 0 },
  USDT: { symbol: "USDT", name: "Tether", logo: `${CG}/325/large/Tether.png`, color: "#26a17b", kind: "stable", price: "$1.00", change24h: 0 },
};

// ---------------------------------------------------------------------------
// Tokenized stocks
// ---------------------------------------------------------------------------
export const STOCKS: Record<string, Asset> = {
  AAPL: { symbol: "AAPL", name: "Apple", logo: "/assets/stocks/apple.svg", color: "#111111", kind: "stock", price: "$214.80", change24h: 0.9 },
  TSLA: { symbol: "TSLA", name: "Tesla", logo: "/assets/stocks/tesla.svg", color: "#e82127", kind: "stock", price: "$248.50", change24h: 3.4 },
  NVDA: { symbol: "NVDA", name: "NVIDIA", logo: "/assets/stocks/nvidia.svg", color: "#76b900", kind: "stock", price: "$126.40", change24h: 2.7 },
  MSFT: { symbol: "MSFT", name: "Microsoft", logo: "/assets/stocks/microsoft.svg", color: "#2f6fed", kind: "stock", price: "$441.20", change24h: 0.6 },
  GOOGL: { symbol: "GOOGL", name: "Alphabet", logo: "/assets/stocks/google.svg", color: "#4285f4", kind: "stock", price: "$178.10", change24h: 1.2 },
  AMZN: { symbol: "AMZN", name: "Amazon", logo: "/assets/stocks/amazon.svg", color: "#ff9900", kind: "stock", price: "$186.30", change24h: -0.4 },
  META: { symbol: "META", name: "Meta", logo: "/assets/stocks/meta.svg", color: "#0866ff", kind: "stock", price: "$502.10", change24h: 1.9 },
  COIN: { symbol: "COIN", name: "Coinbase", logo: "/assets/stocks/coinbase.svg", color: "#0052ff", kind: "stock", price: "$224.70", change24h: 4.8 },
  HOOD: { symbol: "HOOD", name: "Robinhood", logo: "/assets/stocks/robinhood.svg", color: "#00c805", kind: "stock", price: "$22.10", change24h: 2.2 },
  MSTR: { symbol: "MSTR", name: "MicroStrategy", logo: "/assets/stocks/microstrategy.svg", color: "#f7931a", kind: "stock", price: "$1,640", change24h: 6.1 },
};

// ---------------------------------------------------------------------------
// Chains (cross-chain story)
// ---------------------------------------------------------------------------
export const CHAINS: Chain[] = [
  { id: "ethereum", name: "Ethereum", logo: `${CHAIN}/ethereum/info/logo.png`, color: "#627eea" },
  { id: "solana", name: "Solana", logo: `${CHAIN}/solana/info/logo.png`, color: "#9945ff" },
  { id: "base", name: "Base", logo: `${CHAIN}/base/info/logo.png`, color: "#0052ff" },
  { id: "arbitrum", name: "Arbitrum", logo: `${CHAIN}/arbitrum/info/logo.png`, color: "#28a0f0" },
  { id: "polygon", name: "Polygon", logo: `${CHAIN}/polygon/info/logo.png`, color: "#8247e5" },
  { id: "bitcoin", name: "Bitcoin", logo: `${CHAIN}/bitcoin/info/logo.png`, color: "#f7931a" },
  { id: "optimism", name: "Optimism", logo: `${CHAIN}/optimism/info/logo.png`, color: "#ff0420" },
  { id: "bsc", name: "BNB Chain", logo: `${CHAIN}/smartchain/info/logo.png`, color: "#f0b90b" },
];

// ---------------------------------------------------------------------------
// Baskets (trade the narrative)
// ---------------------------------------------------------------------------
export const BASKETS: Basket[] = [
  {
    id: "ai-renaissance",
    name: "AI Renaissance",
    tagline: "The compute-and-models supercycle, tokenized.",
    change7d: 12.4,
    accent: "#a855f7",
    constituents: [STOCKS.NVDA, STOCKS.MSFT, CRYPTO.RENDER, CRYPTO.LINK, STOCKS.GOOGL],
  },
  {
    id: "mag7-tokenized",
    name: "Mag-7 Tokenized",
    tagline: "Big Tech, onchain and tradable 24/7.",
    change7d: 4.8,
    accent: "#3b82f6",
    constituents: [STOCKS.AAPL, STOCKS.MSFT, STOCKS.NVDA, STOCKS.AMZN, STOCKS.META, STOCKS.GOOGL],
  },
  {
    id: "rwa-bluechips",
    name: "RWA Blue-Chips",
    tagline: "Real-world assets meeting onchain liquidity.",
    change7d: 6.1,
    accent: "#22c55e",
    constituents: [CRYPTO.ONDO, CRYPTO.LINK, STOCKS.COIN, CRYPTO.ETH],
  },
  {
    id: "solana-degens",
    name: "Solana Degens",
    tagline: "High-beta Solana culture in one tap.",
    change7d: 18.9,
    accent: "#14f195",
    constituents: [CRYPTO.SOL, CRYPTO.BONK, CRYPTO.WIF, CRYPTO.JUP],
  },
  {
    id: "digital-gold",
    name: "Digital Gold",
    tagline: "The store-of-value thesis, leveraged.",
    change7d: 3.2,
    accent: "#f7931a",
    constituents: [CRYPTO.BTC, STOCKS.MSTR, STOCKS.COIN],
  },
];

// ---------------------------------------------------------------------------
// Prediction markets
// ---------------------------------------------------------------------------
export const PREDICTIONS: PredictionMarket[] = [
  { id: "btc-100k", question: "Will BTC close above $100k this year?", category: "Crypto", yes: 64, volume: "$4.2M", endsIn: "Dec 31" },
  { id: "fed-cut", question: "Fed cuts rates at the next meeting?", category: "Macro", yes: 41, volume: "$2.8M", endsIn: "18d" },
  { id: "eth-etf", question: "Spot ETH ETF net inflows positive in Q3?", category: "Crypto", yes: 72, volume: "$1.6M", endsIn: "Sep 30" },
  { id: "ai-ipo", question: "A major AI lab files to IPO this year?", category: "Tech", yes: 33, volume: "$980K", endsIn: "Dec 31" },
];

// ---------------------------------------------------------------------------
// Trust / scale stats (CONFIRM before launch)
// ---------------------------------------------------------------------------
export const STATS: Stat[] = [
  { label: "Chains connected", value: 20, suffix: "+" },
  { label: "Assets tradable", value: 12000, suffix: "+" },
  { label: "Total volume", value: 1.8, prefix: "$", suffix: "B+" },
  { label: "Traders onboarded", value: 250, suffix: "K+" },
];

// Convenience collections for tickers / streams.
export const ALL_ASSETS: Asset[] = [
  ...Object.values(CRYPTO),
  ...Object.values(STOCKS),
  ...Object.values(STABLES),
];

export const TICKER_ASSETS: Asset[] = [
  CRYPTO.BTC, STOCKS.NVDA, CRYPTO.SOL, STOCKS.TSLA, CRYPTO.ETH, STOCKS.AAPL,
  CRYPTO.BONK, STOCKS.COIN, CRYPTO.JUP, STOCKS.MSFT, CRYPTO.WIF, STOCKS.HOOD,
];
